import React, { FC, useEffect, useRef } from 'react';
import { FREQUENCY_AUTO_ADVANCE_DELAY_MS } from '../../data/constants';
import type { QuestionProps } from './types';

export const FrequencyQuestion: FC<QuestionProps> = ({
  question, answers, setAnswers, onComplete,
}) => {
  const currentAnswer = answers[question.id];
  const previousAnswer = useRef(currentAnswer);

  const handleSelect = (v: string) => {
    setAnswers((p) => ({ ...p, [question.id]: v }));
  };

  // Auto-advance via useEffect so the pending timer is cancelled
  // when the user re-selects or the component unmounts.
  useEffect(() => {
    if (currentAnswer && currentAnswer !== previousAnswer.current) {
      previousAnswer.current = currentAnswer;
      const timer = setTimeout(
        onComplete,
        FREQUENCY_AUTO_ADVANCE_DELAY_MS,
      );
      return () => clearTimeout(timer);
    }
    previousAnswer.current = currentAnswer;
    return undefined;
  }, [currentAnswer, onComplete]);

  const options = question.options || [];

  return (
    <div>
      <p className="text-base font-bold text-slate-800 mb-2">{question.text}</p>
      <p className="text-xs text-slate-400 mb-5">
        How frequently do you experience this?
      </p>
      <div className="flex flex-col gap-3">
        {options.map((o) => {
          const optValue = String(o.value);
          const sel = currentAnswer === optValue;
          return (
            <button
              key={optValue}
              type="button"
              onClick={() => handleSelect(optValue)}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                sel
                  ? 'border-brand bg-brand bg-opacity-5 text-brand'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  sel ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
                }`}
              >
                {sel && (
                  <span className="w-2 h-2 rounded-full bg-white block" />
                )}
              </span>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FrequencyQuestion;
